import type { HTMLAttributes } from 'react'
import styles from './FillMeter.module.css'

export interface FillMeterProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  /** How many places are taken. Clamped into `[0, max]`. */
  value: number
  /** Capacity. A `max` of zero or less means "uncapped" — the track renders empty. */
  max: number
  /** Leading caption, e.g. `22 of 32 teams`. Rendered above the track. */
  label?: string
  /** Trailing caption, e.g. `10 places left`. Rendered opposite `label`. */
  remainingLabel?: string
  /** Track thickness. `sm` is 2px (table rows), `md` is 3px (spotlight). */
  size?: 'sm' | 'md'
  /**
   * Colour of the fill. Defaults to monochrome, deliberately — see below.
   *
   * A tone is for a meter whose colour *is* the information: a risk band, a
   * roster-health ratio, a quota about to be breached. Reach for it when the
   * reader is meant to judge the value, not just read it. A capacity bar is
   * not that, and should stay `default`.
   */
  tone?: 'default' | 'success' | 'warning' | 'critical'
  /**
   * Accessible name for the bar itself. Falls back to `label`; supply this when
   * `label` is omitted, or the progressbar announces as unnamed.
   */
  'aria-label'?: string
}

/**
 * A capacity bar, deliberately monochrome.
 *
 * Registration scarcity is the one thing a season card has to communicate at a
 * glance, and it used to be a bare `22/32` that made the reader do the
 * arithmetic. This draws it instead — but in `--nexus-text` on
 * `--nexus-secondary`, never a brand gradient. Colour on this surface is
 * reserved for the primary action and for an org's own branding; a meter that
 * glows competes with both and stops meaning anything.
 */
export function FillMeter({
  value,
  max,
  label,
  remainingLabel,
  size = 'md',
  tone = 'default',
  className,
  'aria-label': ariaLabel,
  ...props
}: FillMeterProps) {
  const capped = max > 0
  // A season with no cap still renders — an empty track reads as "room left",
  // which is true, and is better than the bar disappearing between renders.
  const safeMax = capped ? max : 0
  const clamped = capped ? Math.min(Math.max(value, 0), safeMax) : 0
  const percent = capped ? (clamped / safeMax) * 100 : 0

  const classes = [
    styles.root,
    styles[size],
    tone !== 'default' && styles[tone],
    className,
  ].filter(Boolean).join(' ')

  return (
    <div className={classes} {...props}>
      {(label || remainingLabel) && (
        <div className={styles.head}>
          {label && <span className={styles.label}>{label}</span>}
          {remainingLabel && <span className={styles.remaining}>{remainingLabel}</span>}
        </div>
      )}
      <div
        className={styles.track}
        role="progressbar"
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-label={ariaLabel ?? label}
      >
        <div className={styles.fill} style={{ width: `${percent}%` }} />
      </div>
    </div>
  )
}
